import { FileWarning } from 'lucide-react';
import type { MerchantDocument } from '../../api/types';

type DocumentPreviewProps = {
  document: MerchantDocument | null;
};

function isImageMime(mime?: string, url?: string | null): boolean {
  if (mime?.startsWith('image/')) return true;
  if (url?.startsWith('data:image/')) return true;
  const lower = url?.split('?')[0]?.toLowerCase() ?? '';
  return /\.(png|jpe?g|gif|webp)$/i.test(lower);
}

function isTruncatedDataUrl(url: string): boolean {
  return url.startsWith('data:') && (url.includes('\u2026') || url.length < 200);
}

export function DocumentPreview({ document }: DocumentPreviewProps) {
  if (!document) {
    return (
      <div className="doc-preview-empty">
        <p className="modal-muted">Select a document to preview</p>
      </div>
    );
  }

  if (!document.url) {
    return (
      <div className="doc-preview-empty">
        <FileWarning size={32} color="var(--onyx-amber)" aria-hidden />
        <p className="modal-muted">Document not uploaded</p>
      </div>
    );
  }

  if (isTruncatedDataUrl(document.url)) {
    return (
      <div className="doc-preview-empty">
        <FileWarning size={32} color="var(--onyx-amber)" aria-hidden />
        <p className="modal-muted">
          Document was saved incompletely. Ask the merchant to re-upload this file in the merchant dashboard.
        </p>
      </div>
    );
  }

  if (isImageMime(document.mime_type, document.url)) {
    return (
      <div className="doc-preview-frame">
        <img src={document.url} alt={document.label} className="doc-preview-image" />
      </div>
    );
  }

  return (
    <div className="doc-preview-frame">
      <object data={document.url} type="application/pdf" className="doc-preview-object" title={document.label}>
        <iframe src={document.url} className="doc-preview-object" title={document.label} />
      </object>
    </div>
  );
}
